import React, { useState, useEffect, useRef } from "react";
import { FaPlus, FaTimes, FaSearch, FaEdit } from "react-icons/fa";
import toast, { Toaster } from "react-hot-toast";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import displayINRCurrency from "../helpers/displayCurrency";
import SummaryApi from "../common/index";
import { motion } from "framer-motion";
import { CheckCircle, XCircle, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
const AllPackages = () => {
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editPackage, setEditPackage] = useState(null);
  const [packages, setPackages] = useState([]);
  const [activePlans, setActivePlans] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [saving, setSaving] = useState(false);
  const editorRef = useRef(null);
  const quillInstance = useRef(null);
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
const itemsPerPage = 6; // 4 columns * 2 rows = 8 items
  // Pink bullets only in display cards (not in editor)
useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .package-specifications ul {
        list-style: none;
        padding-left: 0;
        margin: 0.4rem 0;
      }
      /* Only apply pink bullet to top-level items */
      .package-specifications > ul > li {
        display: flex;
        align-items: flex-start;
        margin-bottom: 0.5rem;
        line-height: 1.4;
        font-size: 0.875rem;
      }
      .package-specifications > ul > li::before {
        content: "•";
        color: #ec4899;
        font-weight: bold;
        margin-right: 0.75rem;
        flex-shrink: 0;
        margin-top: 0.1rem;
      }

      /* Handle Nested Lists (Sub-points) */
      .package-specifications li ul {
        list-style: disc; /* Standard bullet for sub-points */
        padding-left: 1.5rem;
        margin-top: 0.25rem;
        margin-bottom: 0.5rem;
        width: 100%;
      }
      .package-specifications li ul li {
        display: list-item; /* Reset from flex */
        color: #4b5563; /* Slightly lighter text for sub-points */
      }
      .package-specifications li ul li::before {
        content: none; /* Remove pink bullet from sub-points */
      }

      .ql-editor strong, .ql-editor b { font-weight: bold !important; } 
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
}, []);


  useEffect(() => {
    fetchAllPackages();
    fetchActivePlans();
  }, []);

  useEffect(() => {
    if (modalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [modalOpen]);

  const fetchAllPackages = async () => {
    setLoadingPackages(true);
    try {
      const response = await fetch(SummaryApi.getAllPackage.url, {
        method: SummaryApi.getAllPackage.method,
        headers: { "content-type": "application/json" },
      });
      const result = await response.json();
      if (result.status === true && Array.isArray(result.data)) {
        setPackages(result.data);
      } else {
        showErrorToast("Failed to load packages");
        setPackages([]);
      }
    } catch (error) {
      console.error("Error fetching packages:", error);
      showErrorToast("Failed to load packages");
      setPackages([]);
    } finally {
      setLoadingPackages(false);
    }
  };

  const fetchActivePlans = async () => {
    setLoadingPlans(true);
    try {
      const response = await fetch(SummaryApi.getActivePlan.url, {
        method: SummaryApi.getActivePlan.method,
        headers: { "content-type": "application/json" },
      });
      const result = await response.json();
      if (result.status === true && Array.isArray(result.data)) {
        setActivePlans(result.data);
      } else {
        showErrorToast("Failed to load active plans");
        setActivePlans([]);
      }
    } catch (error) {
      console.error("Error fetching active plans:", error);
      showErrorToast("Failed to load plans for selection");
      setActivePlans([]);
    } finally {
      setLoadingPlans(false);
    }
  };

  const filtered = Array.isArray(packages)
    ? packages.filter(
        (p) =>
          (p.package_name || "").toLowerCase().includes(search.toLowerCase()) ||
          (p.title || "").toLowerCase().includes(search.toLowerCase()) ||
          (p.subtitle || "").toLowerCase().includes(search.toLowerCase()),
      )
    : [];

  const showSuccessToast = (title, description) => {
    if (!title || !description) return;

    toast.custom(
      (t) => (
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, scale: 0.5 }}
          className="pointer-events-auto max-w-md w-full bg-white shadow-2xl rounded-[1.5rem] flex border-b-4 border-rose-500"
        >
          <div className="flex-1 p-4">
            <div className="flex items-start gap-3">
              <div className="h-12 w-12 rounded-xl bg-rose-100 flex items-center justify-center text-rose-500">
                <CheckCircle size={28} />
              </div>
              <div>
                <p className="text-sm font-bold text-rose-950">{title}</p>
                <p className="text-xs text-rose-900/60 mt-1">{description}</p>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => toast.dismiss(t.id)}
            className="px-4 font-bold text-rose-600"
          >
            Close
          </button>
        </motion.div>
      ),
      {
        duration: 500,
        // position: "top-center",
      },
    );
  };
  const showLoginToast = () => {
    toast.dismiss("auth-toast");
    toast.custom(
      (t) => (
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, scale: 0.5 }}
          className="pointer-events-auto max-w-md w-full bg-white shadow-2xl rounded-[1.5rem] flex border-b-4 border-amber-500"
        >
          <div className="flex-1 p-4">
            <div className="flex items-start gap-3">
              <div className="h-12 w-12 rounded-xl bg-amber-100 flex items-center justify-center text-amber-600">
                <Lock size={28} />
              </div>
              <div>
                <p className="text-sm font-bold text-amber-950">
                  Login Required
                </p>
                <p className="text-xs text-amber-900/60 mt-1">
                  Please sign in to manage or create packages.
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-col border-l border-gray-100">
            <button
              onClick={() => {
                toast.dismiss(t.id);
                navigate("/login"); // Adjust this path to your actual login route
              }}
              className="flex-1 px-4 text-sm font-bold text-amber-600 hover:bg-amber-50 transition-colors rounded-tr-[1.5rem]"
            >
              Login
            </button>
            <button
              onClick={() => toast.dismiss(t.id)}
              className="flex-1 px-4 text-sm font-bold text-gray-400 hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
          </div>
        </motion.div>
      ),
      { id: "auth-toast", duration: 5000, position: "top-center" },
    );
  };

  const showErrorToast = (message) => {
    if (!message) return;

    toast.custom(
      (t) => (
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, scale: 0.5 }}
          className="pointer-events-auto max-w-md w-full bg-white shadow-2xl rounded-[1.5rem] flex border-b-4 border-red-500"
        >
          <div className="flex-1 p-4">
            <div className="flex items-start gap-3">
              <div className="h-12 w-12 rounded-xl bg-red-100 flex items-center justify-center text-red-500">
                <XCircle size={28} />
              </div>
              <div>
                <p className="text-sm font-bold text-red-950">Error</p>
                <p className="text-xs text-red-900/60 mt-1">{message}</p>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => toast.dismiss(t.id)}
            className="px-4 font-bold text-red-600"
          >
            Close
          </button>
        </motion.div>
      ),
      {
        duration: 500,
        position: "top-center",
      },
    );
  };

  const openCreateModal = () => {
    setEditPackage(null);
    setModalOpen(true);
  };

  const openEditModal = (pkg) => {
    setEditPackage(pkg);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditPackage(null);
    if (quillInstance.current) {
      quillInstance.current.off("text-change");
      quillInstance.current = null;
    }
    if (editorRef.current) {
      editorRef.current.innerHTML = "";
    }
  };
useEffect(() => {
  setCurrentPage(1);
}, [search]);
const indexOfLastItem = currentPage * itemsPerPage;
const indexOfFirstItem = indexOfLastItem - itemsPerPage;
const currentItems = filtered.slice(indexOfFirstItem, indexOfLastItem);
const totalPages = Math.ceil(filtered.length / itemsPerPage);

const handleNextPage = () => {
  if (currentPage < totalPages) setCurrentPage(prev => prev + 1);
};

const handlePrevPage = () => {
  if (currentPage > 1) setCurrentPage(prev => prev - 1);
};
const encodeSpecialChars = (str) => {
  return str.replace(/₹/g, '&#8377;');
};
  const handleSave = async (data) => {
    setSaving(true);
    const finalData = {
    plan_id: data.plan_id ? Number(data.plan_id) : null,
    package_name: data.package_name?.trim() || "",
    title: data.title?.trim() || "",
    subtitle: data.subtitle?.trim() || "",
    actual_price: Number(data.actual_price) || 0, // Sending actual price as price
    selling_price: Number(data.selling_price) || 0,
    no_of_leafs: Number(data.no_of_leafs) || 0,
    discount: Number(data.discount) || 0, // Sending calculated discount
    weight: Number(data.weight) || 0,
   specifications: (data.specifications || "").replace(/₹/g, '&#8377;'),
    is_recommended: data.is_recommended ? 1 : 0,
    is_active: data.is_active ? 1 : 0,
  };

    if (editPackage?.package_id) {
      finalData.package_id = Number(editPackage.package_id);
    }

    const url = editPackage
      ? SummaryApi.UpdatePackage.url
      : SummaryApi.CreatePackage.url;

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(finalData),
      });
      const resData = await res.json();

      if (resData.status === true) {
        const name = editPackage
          ? editPackage.package_name
          : data.package_name || "New Package";
        showSuccessToast(
          editPackage
            ? "Package updated successfully!"
            : "Package created successfully!",
          `Saved successfully`,
        );
        fetchAllPackages();

        setTimeout(() => {
          closeModal();
        }, 150);
      } else {
        showErrorToast(resData.message || "Something went wrong");
      }
    } catch (error) {
      console.error("Save error:", error);
      showErrorToast("Network error. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const PackageModal = () => {
    const isEditMode = !!editPackage;

    const [formData, setFormData] = useState(
      isEditMode
        ? {
            plan_id: editPackage.plan_id || "",
            package_name: editPackage.package_name || "",
            title: editPackage.title || "",
            subtitle: editPackage.subtitle || "",
              actual_price: editPackage.actual_price ?? "", 
        selling_price: editPackage.selling_price ?? "",
            no_of_leafs: editPackage.no_of_leafs || "",
            discount: editPackage.discount || "",
            specifications: editPackage.specifications || "",
            weight: editPackage.weight || "",
            is_recommended: editPackage.is_recommended === 1,
            is_active: editPackage.is_active === 1,
          }
        : {
            plan_id: "",
            package_name: "",
            title: "",
            subtitle: "",
            actual_price: "",
            selling_price: "",
            no_of_leafs: "",
            discount: "",
            specifications: "",
            weight: "",
            is_recommended: false,
            is_active: true,
          },
    );

    const handleChange = (e) => {
      const { name, value, type, checked } = e.target;
      const val = type === "checkbox" ? checked : value;

      setFormData((prev) => {
        const updatedData = { ...prev, [name]: val };

        // Calculate Discount automatically if Actual Price or Selling Price changes
        if (name === "actual_price" || name === "selling_price") {
          const actual =
            name === "actual_price"
              ? parseFloat(val)
              : parseFloat(prev.actual_price);
          const selling =
            name === "selling_price"
              ? parseFloat(val)
              : parseFloat(prev.selling_price);

          if (actual && actual > 0 && selling >= 0) {
            const discountPercentage = ((actual - selling) / actual) * 100;
            updatedData.discount = discountPercentage.toFixed(2);
          } else {
            updatedData.discount = 0;
          }
        }

        return updatedData;
      });
    };

    useEffect(() => {
      if (!editorRef.current || quillInstance.current) return;

      quillInstance.current = new Quill(editorRef.current, {
        theme: "snow",
        modules: {
          toolbar: [
            [{ header: [1, 2, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link"],
            ["clean"],
          ],
        },
        placeholder: "Start typing specifications...",
      });

     const savedSpecs = editPackage?.specifications || "";

if (savedSpecs) {
    // If it already has UL/LI tags, just paste it.
    // If not, wrap it.
    if (savedSpecs.includes("<li")) {
        quillInstance.current.clipboard.dangerouslyPasteHTML(savedSpecs);
    } else {
        quillInstance.current.clipboard.dangerouslyPasteHTML(`<ul><li>${savedSpecs}</li></ul>`);
    }
}

      setTimeout(() => {
        quillInstance.current.format("list", "bullet");
      }, 0);

      quillInstance.current.on("text-change", () => {
        setFormData((prev) => ({
          ...prev,
          specifications: quillInstance.current.root.innerHTML,
        }));
      });

      quillInstance.current.focus();

      return () => {
        quillInstance.current?.off("text-change");
      };
    }, [modalOpen, editPackage]);

    const onSubmit = (e) => {
      e.preventDefault();

      if (!formData.plan_id) {
        showErrorToast("Please select a plan");
        return;
      }

      const rawHtml = quillInstance.current?.root.innerHTML || "";
      const cleanedSpecs = cleanQuillListHTML(rawHtml);

      handleSave({
        ...formData,
        specifications: cleanedSpecs,
      });
    };

const cleanQuillListHTML = (html) => {
  if (!html) return "";

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  // 1. Remove Quill's internal UI elements (the bullet spans)
  const uiElements = tempDiv.querySelectorAll('.ql-ui');
  uiElements.forEach(el => el.remove());


    return tempDiv.innerHTML;
 
};

    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-start justify-center pt-10 px-4 overflow-y-auto"
        onClick={closeModal}
      >
        <div
          className="bg-white rounded-xl shadow-2xl w-full max-w-4xl my-8 max-h-[90vh] overflow-y-auto relative"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 z-30 bg-white border-b px-6 py-4 flex items-center justify-between rounded-t-xl">
            <h2 className="text-xl font-bold text-gray-800">
              {isEditMode ? "Edit Package" : "Create Package"}
            </h2>
            <button
              onClick={closeModal}
              className="rounded-full p-2 hover:bg-gray-100 transition"
              disabled={saving}
            >
              <FaTimes className="text-2xl text-gray-600" />
            </button>
          </div>

          {saving ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-12 h-12 rounded-full border-4 border-pink-500 border-t-transparent animate-spin"></div>
              <p className="mt-4 text-pink-600 font-medium">
                Saving package...
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Plan <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="plan_id"
                    value={formData.plan_id}
                    onChange={handleChange}
                    required
                    disabled={activePlans.length === 0}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none disabled:opacity-60"
                  >
                    <option value="">Select an Active Plan</option>
                    {activePlans.map((plan) => (
                      <option key={plan.plan_id} value={plan.plan_id}>
                        {plan.plan_name} - {plan.duration}
                      </option>
                    ))}
                  </select>
                  {activePlans.length === 0 && (
                    <p className="text-xs text-red-500 mt-1">
                      No active plans available
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Package Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="package_name"
                    value={formData.package_name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none"
                  />
                </div>
                  <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subtitle
                  </label>
                  <input
                    type="text"
                    name="subtitle"
                    value={formData.subtitle}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    No. of Leafs <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="no_of_leafs"
                    value={formData.no_of_leafs}
                    onChange={handleChange}
                    required
                    min="0"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Weight (in gms) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="weight"
                    value={formData.weight}
                    onChange={handleChange}
                    required
                    min="0"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Actual Price (MRP) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="actual_price"
                    value={formData.actual_price}
                    onChange={handleChange}
                    required
                    min="0"
                    step="0.01"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Selling Price <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="selling_price"
                    value={formData.selling_price}
                    onChange={handleChange}
                    required
                    min="0"
                    step="0.01"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none"
                  />
                </div>

                
                <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      Discount (%) <span className="text-gray-400 text-xs">(Auto-calculated)</span>
    </label>
    <input
      type="number"
      name="discount"
      value={formData.discount}
      readOnly
      className="w-full px-4 py-2 border border-gray-100 bg-gray-50 text-gray-500 rounded-lg cursor-not-allowed outline-none font-bold"
    />
  </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Specifications (Rich Text)
                </label>
                <div className="border border-gray-300 rounded-lg overflow-hidden">
                  <div ref={editorRef} className="h-96 bg-white" />
                  <div className="text-xs text-gray-500 p-2 bg-gray-50 border-t border-gray-200">
                    Bullet points are active by default
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-8 pt-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <span className="text-gray-700 font-medium">
                    Recommended Package
                  </span>
                  <div className="relative">
                    <input
                      type="checkbox"
                      name="is_recommended"
                      checked={formData.is_recommended}
                      onChange={handleChange}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-pink-600 transition duration-300 ease-in-out"></div>
                    <div className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition peer-checked:translate-x-5 duration-300 ease-in-out"></div>
                  </div>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <span className="text-gray-700 font-medium">Active</span>
                  <div className="relative">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleChange}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-green-600 transition duration-300 ease-in-out"></div>
                    <div className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition peer-checked:translate-x-5 duration-300 ease-in-out"></div>
                  </div>
                </label>
              </div>

              <div className="flex justify-end pt-6 border-t">
                <button
                  type="submit"
                  disabled={saving}
                  className={`px-8 py-3 bg-pink-600 text-white font-bold rounded-lg transition shadow-md flex items-center gap-2 ${
                    saving
                      ? "opacity-70 cursor-not-allowed"
                      : "hover:bg-pink-700"
                  }`}
                >
                  {saving ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Saving...
                    </>
                  ) : isEditMode ? (
                    "Update Package"
                  ) : (
                    "Create Package"
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    );
  };

 const PackageCard = ({ pkg }) => {
  const [showAll, setShowAll] = useState(false); // State to toggle specifications
  const plan = activePlans.find((p) => p.plan_id === pkg.plan_id);
  const planDuration = plan?.duration || "Custom Plan";
  const totalLeafs = pkg.no_of_leafs || 0;

  const actualPrice = pkg.actual_price || 0;
  const discountPercent = pkg.discount || 0;
  const sellingPrice = actualPrice - (actualPrice * discountPercent) / 100;

  const effectiveRateRaw = totalLeafs > 0 ? sellingPrice / totalLeafs : 0;
  const effectiveRate = Number.isInteger(effectiveRateRaw)
    ? effectiveRateRaw.toString()
    : effectiveRateRaw.toFixed(4).replace(/\.?0+$/, "");

  // Logic to parse HTML specifications into an array of strings
  const getSpecsArray = (htmlString) => {
  if (!htmlString) return [];
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  
  // Find all <li> elements
  const lis = doc.querySelectorAll("li");
  
   if (lis.length > 0) {
    return Array.from(lis).map(li => li.innerHTML);
  }
    const ps = doc.querySelectorAll("p");
  if (ps.length > 0) {
    return Array.from(ps).map(p => p.innerHTML).filter(content => content.trim() !== "");
  }

  // If it's just a string with no <li>, return it as a single item array
  return [htmlString];
};

  const allSpecs = getSpecsArray(pkg.specifications);
  const visibleSpecs = showAll ? allSpecs : allSpecs.slice(0, 6);
  const hasMore = allSpecs.length > 6;

  return (
    <div className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-pink-100 overflow-hidden flex flex-col h-full">
      <div className="relative pt-6 px-6 mt-2">
        {pkg.is_recommended === 1 && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 bg-gradient-to-r from-pink-600 to-pink-500 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg uppercase tracking-wider">
            Recommended
          </div>
        )}
        <div
          className={`absolute top-4 right-4 z-10 flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full ${
            pkg.is_active === 1
              ? "bg-green-50 text-green-700 border border-green-200"
              : "bg-red-50 text-gray-500 border border-gray-200"
          }`}
        >
          <div
            className={`w-1.5 h-1.5 rounded-full ${
              pkg.is_active === 1 ? "bg-green-500" : "bg-red-400"
            }`}
          ></div>
          {pkg.is_active === 1 ? "Active" : "Inactive"}
        </div>
      </div>

      <div className="flex flex-col flex-grow px-6 pb-6 pt-2 mt-4">
        <div className="text-center mb-4">
          <h3 className="text-2xl font-black text-pink-600 mb-2">
            {pkg.package_name || "Unnamed Package"} - <span className="font-italic text-lg">{pkg.title}</span> / <span className="font-semibold">{planDuration}</span>  
          </h3>
        </div>

        <div className="text-center mb-6 flex items-center justify-center space-x-3">
          {discountPercent > 0 ? (
            <>
              <div className="text-lg font-bold text-pink-700">
                {displayINRCurrency(sellingPrice)}
              </div>
              <div className="text-sm text-gray-500 line-through">
                {displayINRCurrency(actualPrice)}
              </div>
              <div className="inline-flex items-center bg-green-50 text-green-700 text-sm font-bold px-3 py-1 rounded-full border border-green-200">
                {discountPercent}% OFF
              </div>
            </>
          ) : (
            <div className="text-lg font-bold text-pink-700">
              {displayINRCurrency(actualPrice)}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-pink-50 rounded-xl p-2 text-center border border-pink-100">
            <div className="text-2xl font-bold text-pink-600">
              ₹{effectiveRate}
            </div>
            <div className="text-xs text-gray-600 mt-1 font-medium">
              Per Pad
            </div>
          </div>
          <div className="bg-blue-50 rounded-xl p-2 text-center border border-blue-100">
            <div className="text-2xl font-bold text-blue-600">
              {totalLeafs.toLocaleString()}
            </div>
            <div className="text-xs text-gray-600 mt-1 font-medium">
              Total Pads
            </div>
          </div>
        </div>

        {(pkg.title || pkg.subtitle) && (
          <div className="text-center mb-6">
            {pkg.title && (
              <h4 className="text-lg font-bold text-pink-700 mb-1">
                {pkg.title}
              </h4>
            )}
            {pkg.subtitle && (
              <p className="text-sm text-gray-600">{pkg.subtitle}</p>
            )}
          </div>
        )}

        <div className="mb-6">
          <div className="bg-gray-50 rounded-xl p-3">
            <div className="text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wider text-center border-bottom border-gray-200 pb-2">
              Specifications
            </div>

            {pkg.weight ? (
              <div className="mb-2 text-sm font-medium text-gray-700 flex items-start">
                <span className="text-pink-500 mr-2">•</span>
                <span><span className="font-bold">Weight:</span> {pkg.weight}gms</span>
              </div>
            ) : null}

            {allSpecs.length > 0 ? (
              <div className="package-specifications">
                <ul>
                  {visibleSpecs.map((spec, index) => (
                    <li 
                        key={index} 
                        dangerouslySetInnerHTML={{ __html: spec }}
                    />
                  ))}
                </ul>
                
                {hasMore && (
                  <button
                    onClick={() => setShowAll(!showAll)}
                    className="mt-2 text-xs font-bold text-pink-600 hover:text-pink-700 flex items-center gap-1 transition-colors w-full justify-center py-1 bg-pink-50 rounded-lg border border-pink-100"
                  >
                    {showAll ? (
                      "Show Less"
                    ) : (
                      `+ ${allSpecs.length - 6} more points...`
                    )}
                  </button>
                )}
              </div>
            ) : !pkg.weight ? (
              <div className="text-center text-gray-500 text-sm py-3 italic">
                N/A
              </div>
            ) : null}
          </div>
        </div>

        <div className="mt-auto pt-2 border-t border-gray-100">
          <button
            onClick={() => openEditModal(pkg)}
            className="w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-pink-600 to-pink-500 text-white font-semibold rounded-xl hover:from-pink-700 hover:to-pink-600 transform hover:scale-[1.02] transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <FaEdit />
            Edit Package
          </button>
        </div>
      </div>
    </div>
  );
};

  return (
    <>


      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pb-20 px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-6 px-6 py-4">
          <h1 className="text-3xl font-extrabold text-pink-800">
            All Packages
          </h1>
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 px-6 py-1 bg-pink-600 hover:bg-pink-700 text-white font-bold rounded-lg shadow-lg transition"
            disabled={loadingPackages || loadingPlans}
          >
            <FaPlus /> Create Package
          </button>
        </div>

        {loadingPackages || loadingPlans ? (
          <div className="flex flex-col items-center justify-center py-20 min-h-[60vh]">
            <div className="w-14 h-14 rounded-full border-4 border-pink-500 border-t-transparent animate-spin"></div>
            <p className="mt-5 text-lg font-medium text-pink-600">
              {loadingPackages ? "Loading packages..." : "Loading plans..."}
            </p>
            <p className="mt-2 text-sm text-gray-500">Please wait a moment</p>
          </div>
        ) : (
          <div className="mx-auto px-2 sm:px-2 lg:px-2">
            <div className="mb-8">
              <div className="relative max-w-md">
                <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search packages by name, title, or subtitle..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-12 pr-4 py-1 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-pink-500 outline-none transition-all duration-300"
                />
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className="text-center py-10">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                  <FaSearch className="text-2xl text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  {search ? "No packages found" : "No packages yet"}
                </h3>
                <p className="text-gray-500 max-w-md mx-auto">
                  {search
                    ? "Try adjusting your search terms or create a new package."
                    : "Get started by creating your first package."}
                </p>
              </div>
            ) : (
              <>
   <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {currentItems.map((pkg) => (
        <PackageCard key={pkg.package_id} pkg={pkg} />
      ))}
    </div>

    {/* PAGINATION CONTROLS */}
    {totalPages > 1 && (
      <div className="flex items-center justify-center mt-12 gap-4">
        <button
          onClick={handlePrevPage}
          disabled={currentPage === 1}
          className={`px-4 py-2 rounded-lg font-bold transition-all ${
            currentPage === 1
              ? "bg-gray-200 text-gray-400 cursor-not-allowed"
              : "bg-white border-2 border-pink-600 text-pink-600 hover:bg-pink-50"
          }`}
        >
          Previous
        </button>

        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-pink-600 text-white font-bold shadow-md">
          {currentPage}
        </div>

        <button
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
          className={`px-4 py-2 rounded-lg font-bold transition-all ${
            currentPage === totalPages
              ? "bg-gray-200 text-gray-400 cursor-not-allowed"
              : "bg-white border-2 border-pink-600 text-pink-600 hover:bg-pink-50"
          }`}
        >
          Next
        </button>
      </div>
    )}
    
    {/* Page info text */}
    <div className="text-center mt-4 text-gray-500 text-sm">
        Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filtered.length)} of {filtered.length} packages
    </div>
  </>
            )}
          </div>
          
        )}

        {modalOpen && <PackageModal />}
      </div>
    </>
  );
};

export default AllPackages;
